import { Reducer } from 'redux';
import { ICalendlyEvent } from 'api-paths-cas';
import { CalendlyEventsState } from './types';
import { StudentActionTypes } from '../types';
import { DataActionTypes } from '../../data/types';

const initialState: CalendlyEventsState = [];

const reducer: Reducer<CalendlyEventsState> = (state = initialState, action) => {
  switch (action.type) {
    case StudentActionTypes.GET_STUDENT_CALENDLY_EVENTS_SUCCESS:
      if (action.payload.calendlyEvents) {
        return Object.values(action.payload.calendlyEvents);
      }
      return state;
    case StudentActionTypes.UPDATE_CALENDLY_EVENT_WS: {
      if (!action.payload.calendlyEvents) return state;

      const changedEvents = action.payload.calendlyEvents as Record<string, ICalendlyEvent>;
      const updatedState = [...state];

      for (const [eventType, calendlyEvent] of Object.entries(changedEvents)) {
        const existingIndex = updatedState.findIndex(e => e.eventType === eventType);
        if (existingIndex >= 0) {
          const existingEvent = updatedState[existingIndex];
          const mergedScheduledEvents = { ...existingEvent.scheduledEvents };

          for (const [eventKey, scheduledEvent] of Object.entries(calendlyEvent.scheduledEvents ?? {})) {
            mergedScheduledEvents[eventKey] = {
              // preserve existing fields first, then apply the partial diff on top
              ...existingEvent.scheduledEvents?.[eventKey],
              ...scheduledEvent,
              // signedUrls are generated at read time and never stored in DynamoDB,
              // so preserve them from existing Redux state when the WS payload omits them
              signedUrls: scheduledEvent.signedUrls ?? existingEvent.scheduledEvents?.[eventKey]?.signedUrls,
            };
          }

          updatedState[existingIndex] = { ...existingEvent, ...calendlyEvent, scheduledEvents: mergedScheduledEvents };
        } else {
          updatedState.push(calendlyEvent);
        }
      }

      return updatedState;
    }
    case StudentActionTypes.TOGGLE_SCHEDULED_INTERVIEW_SUCCESS:
    case StudentActionTypes.SAVE_INTERVIEW_DOCUMENTS_SUCCESS:
    case StudentActionTypes.DELETE_INTERVIEW_FILE_SUCCESS: {
      const calendlyEvent = action.payload.calendlyEvent as ICalendlyEvent | undefined;
      const eventType = action.payload.eventType as string | undefined;

      if (!calendlyEvent) {
        // used only for cancel in case the cancel at student-manager deleted the whole item
        if (eventType) {
          return state.filter(e => e.eventType !== eventType);
        }
        return state;
      }

      const existingIndex = state.findIndex(e => e.eventType === calendlyEvent.eventType);
      if (existingIndex >= 0) {
        const existingEvent = state[existingIndex];
        const mergedScheduledEvents = Object.fromEntries(
          Object.entries(calendlyEvent.scheduledEvents ?? {}).map(([key, scheduledEvent]) => {
            const existingIsProcessingPdf = existingEvent.scheduledEvents?.[key]?.isProcessingPdf;
            // if the API response does not include isProcessingPdf but Redux state has it
            // (e.g. set optimistically when PDF was uploaded), preserve it so the hourglass
            // stays visible while pdf-2-jpg runs
            return [
              key,
              { ...scheduledEvent, isProcessingPdf: scheduledEvent.isProcessingPdf ?? existingIsProcessingPdf },
            ];
          }),
        );
        const updatedState = [...state];
        updatedState[existingIndex] = { ...calendlyEvent, scheduledEvents: mergedScheduledEvents };
        return updatedState;
      }
      return [...state, calendlyEvent];
    }
    case DataActionTypes.UPLOAD_FILE_SUCCESS:
    case StudentActionTypes.DELETE_FILE: {
      const { eventType, eventKey, fileNames, isInterviewDocumentModal } = action.payload;

      if (!isInterviewDocumentModal) {
        return state;
      }

      const eventIndex = state.findIndex(interview => interview.eventType === eventType);

      if (eventIndex >= 0) {
        const updatedEvent = { ...state[eventIndex] };
        const updatedScheduledEvents = { ...updatedEvent.scheduledEvents };

        if (updatedScheduledEvents[eventKey]) {
          // when a new PDF is uploaded, set isProcessingPdf optimistically so the hourglass
          // shows immediately without waiting for the WS batch window (up to 30s delay)
          const existingFileNames = updatedScheduledEvents[eventKey].fileNames ?? [];
          const newlyUploadedFiles = (fileNames as string[]).filter(
            f => !existingFileNames.includes(f) && !f.startsWith('upload--error--'),
          );
          const isNewPdfUploaded =
            action.type === DataActionTypes.UPLOAD_FILE_SUCCESS &&
            newlyUploadedFiles.some(f => f.toLowerCase().endsWith('.pdf'));

          updatedScheduledEvents[eventKey] = {
            ...updatedScheduledEvents[eventKey],
            fileNames,
            ...(isNewPdfUploaded && { isProcessingPdf: true }),
          };
          updatedEvent.scheduledEvents = updatedScheduledEvents;
          const updatedState = [...state];
          updatedState[eventIndex] = updatedEvent;
          return updatedState;
        }
      }

      return state;
    }
    default:
      return state;
  }
};

export { reducer as calendlyEventsReducer };
